import {
  createTheme,
  CSSVariablesResolver,
  HoverCard,
  Loader,
  LoadingOverlay,
  MantineProvider,
  Menu,
  Modal,
  Popover,
  Select,
} from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import React, { useEffect } from 'react';

import { moengage } from '@/components/app/moengage/moengage';
import { usePurgeExpiredRecommendations } from '@/components/app/shared/usePurgeExpiredRecommendations';

import AirbrakeErrorBoundary from './AirbrakeBoundary';
import ApolloClientProvider, { ApolloClientProviderProps } from './ApolloClientProvider';
import CaptchaProvider from './CaptchaProvider';
import { SHARED_COLORS } from './theme-colors';

export type ThemeProviderProps = ApolloClientProviderProps;

const AppProvider: React.FC<ThemeProviderProps> = ({ children, clientOptions }) => {
  usePurgeExpiredRecommendations();

  useEffect(() => {
    const workspaceId =
      document.querySelector('meta[name="moengage-workspace-id"]')?.getAttribute('content') ?? '';

    const env = (document.querySelector('meta[name="moengage-web-env"]')?.getAttribute('content') ??
      'TEST') as 'LIVE' | 'TEST';

    const rawCluster =
      document.querySelector('meta[name="moengage-web-cluster"]')?.getAttribute('content') ?? '';
    const cluster = rawCluster.trim() || undefined;

    moengage.init(workspaceId, env, cluster);
  }, []);

  return (
    <AirbrakeErrorBoundary>
      <ApolloClientProvider clientOptions={clientOptions}>
        <MantineProvider theme={theme} cssVariablesResolver={resolver} defaultColorScheme="light">
          <ModalsProvider>
            <Notifications position="bottom-right" autoClose={2000} zIndex={5010} />
            <CaptchaProvider>{children}</CaptchaProvider>
          </ModalsProvider>
        </MantineProvider>
      </ApolloClientProvider>
    </AirbrakeErrorBoundary>
  );
};

export default AppProvider;

export const theme = createTheme({
  fontSmoothing: false,
  fontFamily: '"Open Sans", sans-serif',
  components: {
    Modal: Modal.extend({
      defaultProps: {
        overlayProps: { backgroundOpacity: 0.4, blur: 15 },
        transitionProps: { duration: 300 },
        zIndex: 5000,
      },
    }),
    Loader: Loader.extend({
      defaultProps: {
        type: 'dots',
        color: 'orange',
      },
    }),
    Select: Select.extend({
      defaultProps: {
        comboboxProps: { zIndex: 5100 },
      },
    }),
    Menu: Menu.extend({
      defaultProps: {
        zIndex: 5200,
      },
    }),
    HoverCard: HoverCard.extend({
      defaultProps: {
        zIndex: 5100,
      },
    }),
    Popover: Popover.extend({
      defaultProps: {
        zIndex: 5300,
      },
    }),
    LoadingOverlay: LoadingOverlay.extend({
      defaultProps: {
        overlayProps: { radius: 'sm', blur: 2, bg: 'transparent' },
      },
    }),
  },

  colors: SHARED_COLORS,
  fontSizes: {
    xxxs: '0.5rem',
    xxs: '0.625rem',
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
  },
  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.25rem',
    xl: '1.5rem',
    xxl: '2rem',
  },
  defaultRadius: 'md',
  radius: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  headings: {
    fontWeight: '400',
    sizes: {
      h1: { fontSize: '2.125rem', lineHeight: '1.3' },
      h2: { fontSize: '1.625rem', lineHeight: '1.35' },
      h3: { fontSize: '1.375rem', lineHeight: '1.4' },
      h4: { fontSize: '1.125rem', lineHeight: '1.45' },
      h5: { fontSize: '1rem', lineHeight: '1.5' },
      h6: { fontSize: '0.875rem', lineHeight: '1.5' },
    },
  },
  cursorType: 'pointer',
});

export const resolver: CSSVariablesResolver = (theme) => ({
  variables: {
    '--snappea-color-body': '#F9F8F7',
    '--snappea-color-text': '#222222',
    '--snappea-font-size-text': theme.fontSizes.md,
  },
  light: {},
  dark: {},
});
